import { makeAutoObservable, runInAction } from "mobx";
import ApiStore from "./ApiStore";
import {
  ClientRole,
  Contact,
  ContactDto,
  ContactType,
  CrmEvent,
  CrmEventDto,
  CrmEventType,
  ExternalCompany,
  ExternalCompanyDto
} from "../api/CRMApiClient";

class CRMStore {
  isLoading = false;
  error: string | null = null;

  contacts: Contact[] = [];
  clientRoles: ClientRole[] = [];
  contactTypes: ContactType[] = [];
  CrmEventTypes: CrmEventType[] = [];
  externalCompanies: ExternalCompany[] = [];
  CrmEvents: CrmEvent[] = [];

  currentContact: Contact | null = null;
  currentCrmEvent: CrmEvent | null = null;

  public readonly ApiStore: ApiStore;
  private readonly api: ApiStore;

  constructor(apiStore: ApiStore) {
    makeAutoObservable(this);
    this.api = apiStore;
    this.ApiStore = apiStore;
  }

  private setLoading = (v: boolean) => { this.isLoading = v; };
  private setError = (e: any) => { this.error = String(e ?? ""); };

  // UPDATED: Enhanced contact methods with performance optimization
  getContacts = async (includeDetails = false) => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getContacts(includeDetails);
      runInAction(() => { this.contacts = list; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  // NEW: Fast method for list views (no related data)
  getContactsFast = async () => {
    return this.getContacts(false);
  };

  // NEW: Detailed method for when you need related data
  getContactsWithDetails = async () => {
    return this.getContacts(true);
  };

  getContactById = async (id: number) => {
    this.setLoading(true);
    try {
      const c = await this.api.CRMApiClient.getContactById(id);
      runInAction(() => { this.currentContact = c; this.setLoading(false); });
      return c;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  addContact = async (dto: ContactDto) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createContact(dto);
      runInAction(() => {
        if (created) this.contacts.push(created);
        this.setLoading(false);
      });
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateContact = async (id: number, dto: ContactDto) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateContact(id, dto);
      if (ok) await this.getContacts(); // Refresh the list
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteContact = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteContact(id);
      if (ok) runInAction(() => { this.contacts = this.contacts.filter(c => c.id !== id); });
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getClientRoles = async () => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getClientRoles();
      runInAction(() => { this.clientRoles = list; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addClientRole = async (dto: { name: string }) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createClientRole(dto);
      runInAction(() => {
        if (created) this.clientRoles.push(created);
        this.setLoading(false);
      });
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateClientRole = async (id: number, dto: { name: string }) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateClientRole(id, dto);
      if (ok) await this.getClientRoles();
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteClientRole = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteClientRole(id);
      if (ok) runInAction(() => { this.clientRoles = this.clientRoles.filter(r => r.id !== id); });
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getContactTypes = async () => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getContactTypes();
      runInAction(() => { this.contactTypes = list; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addContactType = async (dto: { name: string }) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createContactType(dto);
      runInAction(() => {
        if (created) this.contactTypes.push(created);
        this.setLoading(false);
      });
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateContactType = async (id: number, dto: { name: string }) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateContactType(id, dto);
      if (ok) await this.getContactTypes();
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteContactType = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteContactType(id);
      if (ok) runInAction(() => { this.contactTypes = this.contactTypes.filter(t => t.id !== id); });
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getCrmEventTypes = async () => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getCrmEventTypes();
      runInAction(() => { this.CrmEventTypes = list; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addCrmEventType = async (dto: { name: string }) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createCrmEventType(dto);
      runInAction(() => {
        if (created) this.CrmEventTypes.push(created);
        this.setLoading(false);
      });
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateCrmEventType = async (id: number, dto: { name: string }) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateCrmEventType(id, dto);
      if (ok) await this.getCrmEventTypes();
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteCrmEventType = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteCrmEventType(id);
      if (ok) runInAction(() => { this.CrmEventTypes = this.CrmEventTypes.filter(t => t.id !== id); });
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getExternalCompanies = async () => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getExternalCompanies();
      runInAction(() => { this.externalCompanies = list; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  addExternalCompany = async (dto: ExternalCompanyDto) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createExternalCompany(dto);
      runInAction(() => {
        if (created) this.externalCompanies.push(created);
        this.setLoading(false);
      });
      return created;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  updateExternalCompany = async (id: number, dto: ExternalCompanyDto) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateExternalCompany(id, dto);
      if (ok) await this.getExternalCompanies();
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteExternalCompany = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteExternalCompany(id);
      if (ok) runInAction(() => { this.externalCompanies = this.externalCompanies.filter(c => c.id !== id); });
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  getCrmEvents = async (includeDetails = false) => {
    this.setLoading(true);
    try {
      const list = await this.api.CRMApiClient.getCrmEvents(includeDetails);
      runInAction(() => { this.CrmEvents = list; this.setLoading(false); });
      return list;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return [];
    }
  };

  getCrmEventsFast = async () => {
    return this.getCrmEvents(false);
  };

  getCrmEventsWithDetails = async () => {
    return this.getCrmEvents(true);
  };

  getCrmEventById = async (id: number) => {
    this.setLoading(true);
    try {
      const ev = await this.api.CRMApiClient.getCrmEventById(id);
      runInAction(() => { this.currentCrmEvent = ev; this.setLoading(false); });
      return ev;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return null;
    }
  };

  addCrmEvent = async (dto: CrmEventDto) => {
    this.setLoading(true);
    try {
      const created = await this.api.CRMApiClient.createCrmEvent(dto);
      runInAction(() => {
        if (created) this.CrmEvents.push(created);
        this.setLoading(false);
      });
      return created?.id;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return undefined;
    }
  };

  updateCrmEvent = async (id: number, dto: CrmEventDto) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.updateCrmEvent(id, dto);
      if (ok) await this.getCrmEvents(); // Refresh the list
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  deleteCrmEvent = async (id: number) => {
    this.setLoading(true);
    try {
      const ok = await this.api.CRMApiClient.deleteCrmEvent(id);
      if (ok) runInAction(() => { this.CrmEvents = this.CrmEvents.filter(e => e.id !== id); });
      runInAction(() => { this.setLoading(false); });
      return ok;
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
      return false;
    }
  };

  loadAllData = async () => {
    this.setLoading(true);
    try {
      await Promise.all([
        this.getContactsFast(),      // Fast loading for lists
        this.getClientRoles(),
        this.getContactTypes(),
        this.getCrmEventTypes(),
        this.getExternalCompanies(),
        this.getCrmEventsFast(),     // Fast loading for lists
      ]);
      runInAction(() => { this.setLoading(false); });
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
    }
  };

  loadDetailedData = async () => {
    this.setLoading(true);
    try {
      await Promise.all([
        this.getContactsWithDetails(), // Detailed loading with related data
        this.getClientRoles(),
        this.getContactTypes(),
        this.getCrmEventTypes(),
        this.getExternalCompanies(),
        this.getCrmEventsWithDetails(), // Detailed loading with related data
      ]);
      runInAction(() => { this.setLoading(false); });
    } catch (err) {
      runInAction(() => { this.setError(err); this.setLoading(false); });
    }
  };
}

export default CRMStore;